export interface AIAnswerContent {
  mainQuestion: string;
  answer: string;
  leftBlock: { title: string; items: string[] };
  rightBlock: { title: string; items: string[] };
}

interface Props {
  content: AIAnswerContent;
}

const quadrants = [
  { id: 'what',  icon: 'ri-tools-line',             label: 'What We Do' },
  { id: 'why',   icon: 'ri-shield-star-line',        label: 'Why Choose Us' },
  { id: 'how',   icon: 'ri-arrow-right-circle-line', label: 'How It Works' },
  { id: 'where', icon: 'ri-map-pin-2-fill',          label: 'Where We Serve' },
];

const processItems = [
  'Call or request online — fast response',
  'Camera inspection & clear written estimate',
  'Licensed technician completes the work',
  'Verified resolution + workmanship guarantee',
];

const areaItems = [
  'San Bernardino & Highland',
  'Riverside & Moreno Valley',
  'Fontana, Rialto & Colton',
  'Redlands, Loma Linda & Upland',
  'Rancho Cucamonga area',
  'All San Bernardino County cities',
];

const AIAnswerBlock = ({ content }: Props) => {
  const { mainQuestion, answer, leftBlock, rightBlock } = content;

  return (
    <section className="bg-white overflow-hidden border-t border-surface-border">
      <div className="flex flex-col lg:flex-row min-h-[640px]">

        {/* ── LEFT TEAL ACCENT PANEL ──────────────────────── */}
        <div
          className="lg:w-[300px] flex-shrink-0 relative overflow-hidden flex flex-col"
          style={{ background: 'linear-gradient(148deg, #1A4A52 0%, #1D5460 55%, #235F6A 100%)' }}
        >
          {/* Subtle radial glow */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 160% 90% at -8% 45%, rgba(201,168,76,0.18) 0%, transparent 58%)' }} />
          {/* Right border accent */}
          <div className="absolute right-0 top-0 bottom-0 w-px"
            style={{ background: 'linear-gradient(180deg, transparent, rgba(201,168,76,0.5) 28%, rgba(201,168,76,0.35) 72%, transparent)' }} />

          <div className="relative z-10 px-9 py-14 flex-1 flex flex-col justify-between">
            <div>
              <div className="w-[52px] h-[52px] flex items-center justify-center rounded-2xl mb-8 flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <i className="ri-robot-2-line text-white text-xl" />
              </div>
              <div className="text-white/80 text-[10px] tracking-[0.34em] uppercase font-sans font-semibold mb-4">
                AI &amp; Voice Optimized
              </div>
              <h3 className="font-serif text-white font-bold text-[1.5rem] leading-snug mb-5">
                Structured for AI Search
              </h3>
              <p className="text-white/80 text-[13px] font-sans leading-relaxed mb-10">
                Extractable content optimized for Google AI Overviews, Siri, Alexa, Bing Copilot, and voice search.
              </p>

              <div className="flex flex-col gap-2.5">
                {[
                  { icon: 'ri-google-fill',    label: 'Google AI Overviews' },
                  { icon: 'ri-mic-line',        label: 'Siri & Voice Search' },
                  { icon: 'ri-search-2-line',   label: 'Bing AI & Copilot' },
                ].map(p => (
                  <div key={p.label} className="flex items-center gap-3 opacity-85 hover:opacity-100 transition-opacity cursor-default">
                    <div className="w-6 h-6 flex items-center justify-center rounded border flex-shrink-0"
                      style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.18)' }}>
                      <i className={`${p.icon} text-white/80 text-xs`} />
                    </div>
                    <span className="text-white/85 text-[11px] font-sans">{p.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="font-serif text-white/[0.06] font-bold leading-none select-none overflow-hidden"
              style={{ fontSize: '9rem', marginBottom: '-2.5rem' }}
            >
              &ldquo;
            </div>
          </div>
        </div>

        {/* ── RIGHT CONTENT PANEL ─────────────────────────── */}
        <div className="flex-1 px-8 lg:px-14 xl:px-16 py-14 lg:py-16 overflow-hidden">

          <h2 className="font-serif text-charcoal font-bold leading-tight mb-4"
            style={{ fontSize: 'clamp(1.6rem, 2.5vw, 2.3rem)' }}>
            {mainQuestion}
          </h2>
          <div className="h-[3px] w-14 bg-teal mb-7 rounded-full" />
          <p className="text-charcoal/75 text-[15.5px] leading-[2.05] font-sans mb-12 max-w-[700px]">
            {answer}
          </p>

          {/* ── 4-QUADRANT GRID ─────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">

            {/* Q1: What We Do */}
            <div className="group rounded-2xl p-6 border border-surface-border bg-white transition-all duration-300 hover:border-teal/20 hover:shadow-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: 'rgba(26,74,82,0.08)', border: '1px solid rgba(26,74,82,0.12)' }}>
                  <i className={`${quadrants[0].icon} text-teal text-sm`} />
                </div>
                <h3 className="font-serif text-charcoal font-semibold text-[16px]">{leftBlock.title}</h3>
              </div>
              <ul className="space-y-2">
                {leftBlock.items.map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-charcoal/75 text-[13px] font-sans">
                    <span className="w-1.5 h-1.5 bg-teal/50 rounded-full flex-shrink-0 group-hover:bg-teal transition-colors" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Q2: Why Choose Us */}
            <div className="group rounded-2xl p-6 border border-surface-border bg-white transition-all duration-300 hover:border-teal/20 hover:shadow-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: 'rgba(26,74,82,0.08)', border: '1px solid rgba(26,74,82,0.12)' }}>
                  <i className={`${quadrants[1].icon} text-teal text-sm`} />
                </div>
                <h3 className="font-serif text-charcoal font-semibold text-[16px]">{rightBlock.title}</h3>
              </div>
              <ul className="space-y-2">
                {rightBlock.items.map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-charcoal/75 text-[13px] font-sans">
                    <span className="w-1.5 h-1.5 bg-teal/50 rounded-full flex-shrink-0 group-hover:bg-teal transition-colors" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Q3: How It Works */}
            <div className="group rounded-2xl p-6 border border-surface-border bg-white transition-all duration-300 hover:border-teal/20 hover:shadow-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: 'rgba(26,74,82,0.08)', border: '1px solid rgba(26,74,82,0.12)' }}>
                  <i className={`${quadrants[2].icon} text-teal text-sm`} />
                </div>
                <h3 className="font-serif text-charcoal font-semibold text-[16px]">How It Works</h3>
              </div>
              <ol className="space-y-2">
                {processItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-charcoal/75 text-[13px] font-sans">
                    <span className="font-serif text-teal font-bold text-[11px] mt-0.5 flex-shrink-0 w-4 group-hover:text-teal transition-colors">{i + 1}.</span>
                    {item}
                  </li>
                ))}
              </ol>
            </div>

            {/* Q4: Where We Serve */}
            <div className="group rounded-2xl p-6 border border-surface-border bg-white transition-all duration-300 hover:border-teal/20 hover:shadow-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: 'rgba(26,74,82,0.08)', border: '1px solid rgba(26,74,82,0.12)' }}>
                  <i className={`${quadrants[3].icon} text-teal text-sm`} />
                </div>
                <h3 className="font-serif text-charcoal font-semibold text-[16px]">Where We Serve</h3>
              </div>
              <ul className="space-y-2">
                {areaItems.map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-charcoal/75 text-[13px] font-sans">
                    <span className="w-1.5 h-1.5 bg-teal/50 rounded-full flex-shrink-0 group-hover:bg-teal transition-colors" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Trust chip strip */}
          <div className="flex flex-wrap items-center gap-2">
            {[
              { icon: 'ri-shield-check-fill', label: 'CA Licensed & Insured' },
              { icon: 'ri-alarm-warning-line', label: '24/7 Available' },
              { icon: 'ri-price-tag-3-line',  label: 'Free Estimates' },
              { icon: 'ri-award-line',         label: 'Work Guaranteed' },
              { icon: 'ri-map-pin-2-fill',     label: 'Locally Owned' },
            ].map(chip => (
              <div
                key={chip.label}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-[11.5px] font-sans cursor-default border border-surface-border bg-surface-soft hover:border-teal/25 hover:bg-teal-50 transition-all duration-200 text-charcoal/55 hover:text-teal"
              >
                <i className={`${chip.icon} text-[11px] text-teal/70`} />
                {chip.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIAnswerBlock;
